import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InputSelect,
  Switch,
  Checkbox,
  RadioGroupActive,
  RadioGroupTab,
} from '@teo/components';
import { selectTrainingType } from '@/components/LearningPath/constants/constantsLearningPaths';
import { axios } from '@/axios';
import { config } from '@/config';
import { categoriesIcon } from '../../util';

const SettingsLP = ({
  learningpathId,
  nodeSelected,
  trainingDetail,
  handleChangeNode,
  handleChangeTraining,
  edges,
  setEdges,
  nodes,
  setNodes,
  setTypeOfTraining,
  typeOfTraining,
  setTrainingContentLinking,
  trainingContentLinking,
  themeDetail,
  setThemeDetail,
  categories,
  allCategories,
}) => {
  const { t } = useTranslation();
  const [mandatory, setMandatory] = useState(nodeSelected?.data?.mandatory);
  const [progress, setProgress] = useState(nodeSelected?.data?.blocking);
  const [notCompleted, setNotCompleted] = useState(
    nodeSelected?.data?.notCompleted
  );
  const [notPassed, setNotPassed] = useState(nodeSelected?.data?.notPassed);

  const [typeOfOrder, setTypeOfOrder] = useState(null);
  const [lpCategory, setLpCategory] = useState(null);

  useEffect(() => {
    if (themeDetail?.tags && allCategories) {
      const learningpathTags = allCategories?.find(
        (el) => el?.label === themeDetail?.tags.split(':')[1]
      );
      setLpCategory(learningpathTags);
    }
    if (themeDetail) {
      setTypeOfOrder(themeDetail?.order || '');
    }
  }, [allCategories, themeDetail]);

  function updateMandatoryWithEdges({
    nodes,
    edges,
    startNodeId,
    newMandatory,
  }) {
    const nodeById = new Map(
      nodes.map((n) => [
        n.id,
        {
          ...n,
          data: { ...n.data },
        },
      ])
    );

    const childrenMap = new Map();
    const parentsMap = new Map();

    edges.forEach(({ source, target }) => {
      if (!childrenMap.has(source)) childrenMap.set(source, []);
      if (!parentsMap.has(target)) parentsMap.set(target, []);
      childrenMap.get(source).push(target);
      parentsMap.get(target).push(source);
    });

    const resolveMandatory = (nodeId) => {
      const parents = parentsMap.get(nodeId) || [];
      if (parents.length === 0) {
        return nodeById.get(nodeId)?.data?.mandatory ?? false;
      }
      return parents.some((pid) => nodeById.get(pid)?.data?.mandatory === true);
    };

    const startNode = nodeById.get(startNodeId);
    if (!startNode) return { nodes, edges };

    startNode.data.mandatory = newMandatory;

    const queue = [...(childrenMap.get(startNodeId) || [])];
    const visited = new Set();

    const changedNodes = [startNode];

    while (queue.length) {
      const id = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);

      const node = nodeById.get(id);
      if (!node) continue;

      const prev = node.data.mandatory;
      const next = resolveMandatory(id);

      if (prev !== next) {
        node.data.mandatory = next;
        changedNodes.push(node);
      }

      queue.push(...(childrenMap.get(id) || []));
    }

    const updatedEdges = edges.map((edge) => {
      const targetNode = nodeById.get(edge.target);
      if (!targetNode) return edge;

      return {
        ...edge,
        animated: targetNode.data.mandatory === false,
      };
    });

    changedNodes.forEach((node) => {
      handleChangeNode?.(
        node.id,
        { mandatory: node.data.mandatory },
        node.data?.learningPathId
      );
    });

    return {
      nodes: Array.from(nodeById.values()),
      edges: updatedEdges,
    };
  }

  const handleChangeState = (setFun, isCheck, nodeState, state) => {
    setFun(isCheck);
    if (nodeSelected) {
      if (learningpathId === nodeSelected?.data?.learningPathId) {
        setNodes(
          nodes?.map((node) =>
            node.id === nodeSelected.id
              ? { ...node, data: { ...node?.data, ...nodeState } }
              : node
          )
        );
      } else {
        setNodes(
          nodes?.map((node) =>
            node.data?.nodeID === nodeSelected?.data?.learningPathId
              ? {
                  ...node,
                  data: {
                    ...node?.data,
                    child: node?.data?.child?.map((el) =>
                      el?.id === nodeSelected.id ? { ...el, ...nodeState } : el
                    ),
                  },
                }
              : nodeSelected?.id === node?.id
              ? { ...node, data: { ...node?.data, ...nodeState } }
              : node
          )
        );
      }
      if (state === 'mandatory') {
        if (learningpathId === nodeSelected?.data?.learningPathId) {
          const { nodes: updatedNodes, edges: updatedEdges } =
            updateMandatoryWithEdges({
              nodes,
              edges,
              startNodeId: nodeSelected.id,
              newMandatory: isCheck,
            });

          setNodes(updatedNodes);
          setEdges(updatedEdges);
        } else {
          handleChangeNode(
            nodeSelected?.id,
            nodeState,
            nodeSelected?.data?.learningPathId
          );
        }
      }
      if (state !== 'mandatory') {
        let newNodeState = nodeState;
        if (state === 'blocking') {
          if (isCheck) {
            newNodeState = { ...newNodeState, notCompleted: true };
            setNotCompleted(true);
          } else {
            newNodeState = {
              ...newNodeState,
              notCompleted: false,
              notPassed: false,
            };
            setNotCompleted(false);
            setNotPassed(false);
          }
        }

        if (state === 'notCompleted' && !isCheck) {
          if (!notPassed) {
            newNodeState = { ...newNodeState, blocking: false };
            setProgress(false);
          }
        }

        if (state === 'notPassed' && !isCheck) {
          if (!notCompleted) {
            newNodeState = { ...newNodeState, blocking: false };
            setProgress(false);
          }
        }

        handleChangeNode(
          nodeSelected?.id,
          newNodeState,
          nodeSelected?.data?.learningPathId
        );
      }
    }
  };

  const handleMandatory = (isCheck) => {
    handleChangeState(
      setMandatory,
      isCheck,
      { mandatory: isCheck },
      'mandatory'
    );
  };

  const handleProgress = (isCheck) => {
    handleChangeState(setProgress, isCheck, { blocking: isCheck }, 'blocking');
  };
  const handleNotCompleted = (isCheck) => {
    handleChangeState(
      setNotCompleted,
      isCheck,
      { notCompleted: isCheck },
      'notCompleted'
    );
  };
  const handleNotPassed = (isCheck) => {
    handleChangeState(
      setNotPassed,
      isCheck,
      { notPassed: isCheck },
      'notPassed'
    );
  };

  const onChangeRadioBox = (value) => {
    setTrainingContentLinking(value);
    handleChangeTraining({ contentLinkingType: value });
  };

  const habdleChangeCategory = (value) => {
    setLpCategory(value);
    axios.put(`${config.backend}/learningpath/${nodeSelected?.data?.nodeID}`, {
      tags: `category:${value?.label}`,
    });
    setNodes(
      nodes?.map((node) =>
        node.id === nodeSelected.id
          ? {
              ...node,
              data: { ...node.data, tags: `category:${value?.label}` },
            }
          : node
      )
    );
  };

  return (
    <div className="border-t px-5 py-5">
      <h4 className="mb-1 text-base">
        {t('pages.learning_path.settings_label')}
      </h4>
      {trainingDetail?.trainingType === 'training' && (
        <>
          <div className="mt-5">
            <label className="mb-1 block text-sm font-semibold text-black">
              {t('pages.learning_path.settings.training_type')}
            </label>
            <InputSelect
              value={{
                id: 0,
                value: typeOfTraining,
                name: typeOfTraining,
              }}
              onChange={(value) => {
                setTypeOfTraining(value?.value);
                handleChangeTraining({ typeOfTraining: value?.value });
              }}
              options={selectTrainingType}
            >
              {(options) => (
                <span>
                  {options &&
                    t(`pages.learning_path.settings.type_tr_${options?.name}`)}
                </span>
              )}
            </InputSelect>
          </div>
          {typeOfTraining === 'online' && (
            <div className="mt-5">
              <label className="mb-1 block text-sm font-semibold text-black">
                {t('pages.learning_path.settings.content_linking')}
              </label>
              <RadioGroupActive
                onChange={onChangeRadioBox}
                value={trainingContentLinking}
                gap="gap-2"
              >
                <RadioGroupActive.Option value="URL" hiddenRadio>
                  {t('pages.learning_path.settings.url')}
                </RadioGroupActive.Option>
                <RadioGroupActive.Option value="LTI" hiddenRadio>
                  {t('pages.learning_path.settings.lti')}
                </RadioGroupActive.Option>
                <RadioGroupActive.Option value="xAPI" hiddenRadio>
                  {t('pages.learning_path.settings.xapi')}
                </RadioGroupActive.Option>
              </RadioGroupActive>
            </div>
          )}
        </>
      )}
      {trainingDetail?.trainingType === 'evaluation' && (
        <>
          <div className="mt-5">
            <label className="mb-1 block text-sm font-semibold text-black">
              {t('pages.learning_path.settings.evaluation_type')}
            </label>
            <RadioGroupTab
              onChange={(value) => {
                setTypeOfTraining(value);
                handleChangeTraining({
                  typeOfTraining: value,
                  minScore:
                    value === 'internal'
                      ? 1
                      : trainingDetail?.inputScore
                      ? 0.5
                      : 1,
                });
              }}
              value={typeOfTraining}
              classBody="bg-grey-02 !p-0.5"
            >
              <div className="flex w-full flex-row gap-0.5">
                <div className={`basis-1/2 rounded-lg`}>
                  <RadioGroupTab.Option
                    value="internal"
                    hiddenRadio
                    checkedClassName="bg-primary-04"
                    className="!block !p-2 text-center"
                  >
                    <span className="text-sm font-medium text-grey-09">
                      {t('pages.learning_path.settings.internal')}
                    </span>
                  </RadioGroupTab.Option>
                </div>
                <div className={`basis-1/2 rounded-lg `}>
                  <RadioGroupTab.Option
                    value="external"
                    hiddenRadio
                    checkedClassName="bg-primary-04"
                    className="!block !p-2 text-center"
                  >
                    <span className="text-sm font-medium text-grey-09">
                      {t('pages.learning_path.settings.external')}
                    </span>
                  </RadioGroupTab.Option>
                </div>
              </div>
            </RadioGroupTab>
          </div>
        </>
      )}
      {nodeSelected?.data?.type === 'learningpath' && (
        <>
          <div className="mt-5 flex flex-col">
            <label className="mb-1 block text-sm font-semibold text-black">
              {t('pages.learning_path.settings.category')}
            </label>
            <InputSelect
              value={lpCategory}
              onChange={(value) => habdleChangeCategory(value)}
              options={categories}
              placeholder={t('pages.learning_path.settings.not_specified')}
            >
              {(options) => {
                const Icon = categoriesIcon(options.icon);
                return (
                  <span className="flex items-center">
                    {options && (
                      <>
                        <Icon className="mr-2 h-5 w-5 min-w-5 text-[#231F20]" />
                        {options?.label}
                      </>
                    )}
                  </span>
                );
              }}
            </InputSelect>
          </div>
          <div className="mt-5 flex flex-col">
            <label className="mb-1 block text-sm font-semibold text-black">
              {t('pages.learning_path.settings.order')}
            </label>
            <RadioGroupTab
              onChange={(value) => {
                setTypeOfOrder(value);
                axios.put(
                  `${config.backend}/learningpath/${nodeSelected?.data?.nodeID}`,
                  {
                    order: value,
                  }
                );
              }}
              value={typeOfOrder}
              classBody="bg-grey-02 !p-0.5"
            >
              <div className="flex w-full flex-row gap-0.5">
                <div className={`basis-1/2 rounded-lg`}>
                  <RadioGroupTab.Option
                    value="fixed"
                    hiddenRadio
                    checkedClassName="bg-primary-04"
                    className="!block !p-2 text-center"
                  >
                    <span className="text-sm font-medium text-grey-09">
                      {t('pages.learning_path.settings.fixed')}
                    </span>
                  </RadioGroupTab.Option>
                </div>
                <div className={`basis-1/2 rounded-lg `}>
                  <RadioGroupTab.Option
                    value="flexible"
                    hiddenRadio
                    checkedClassName="bg-primary-04"
                    className="!block !p-2 text-center"
                  >
                    <span className="text-sm font-medium text-grey-09">
                      {t('pages.learning_path.settings.flexible')}
                    </span>
                  </RadioGroupTab.Option>
                </div>
              </div>
            </RadioGroupTab>
          </div>
        </>
      )}
      <div className="mt-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
        <div>
          <Switch
            checked={mandatory}
            onChange={(isCheck) => handleMandatory(isCheck)}
          />
        </div>
        <div>
          <h4 className="font-normal">{t('pages.learning_path.mandatory')}</h4>
        </div>
      </div>
      <div className="mt-2 flex flex-col rounded-lg border border-grey-02 p-4">
        <div className="flex flex-row items-center gap-4 ">
          <Switch
            checked={progress}
            onChange={(isCheck) => handleProgress(isCheck)}
          />

          <div className="flex flex-col">
            <h4 className="font-normal">
              {t('pages.learning_path.block_progress')}
            </h4>
          </div>
        </div>
        {progress && (
          <>
            <div className="z-1 relative top-px mt-4 -ml-4 h-px w-[calc(100%_+_32px)] bg-[#EAEAED]"></div>
            <div className="relative pt-4">
              <div className="pb-2">
                <Checkbox
                  id="notCompleted"
                  ariaLabel="test"
                  onChange={() => {
                    handleNotCompleted(!notCompleted);
                  }}
                  checked={notCompleted}
                />

                <label
                  htmlFor="notCompleted"
                  className="pl-2.5 text-sm text-grey-08"
                >
                  {t('pages.learning_path.when_completed')}
                </label>
              </div>
              <div className="pb-2">
                <Checkbox
                  id="notPassed"
                  ariaLabel="test"
                  onChange={() => {
                    handleNotPassed(!notPassed);
                  }}
                  checked={notPassed}
                />

                <label
                  htmlFor="notPassed"
                  className="pl-2.5 text-sm text-grey-08"
                >
                  {t('pages.learning_path.not_passed')}
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default SettingsLP;
